import {Page} from "../models/common";
import {MaybeComputedRef, resolveUnref, UseFetchReturn} from "@vueuse/core";
import {
    Photo,
    PhotoSimilarity,
    NewTask,
    UpdateTask,
    NewTaskType,
    Task,
    TaskFilter,
    TaskPropertyDescription,
    TaskType,
    UpdateTaskType,
    TaskDescription,
    ProjectTask,
    TaskLog, TaskLink, SharedTask, CopyTask, ReportPhoto, CopyProject
} from "../models/task";
import {useFetch} from "./fetch";
import {computed} from "vue";
import {useDayJs} from "./dayjs";
import {ReportPerformTask} from "../models/performTask";

export function useTasks(project: MaybeComputedRef<number>, page: MaybeComputedRef<number>, filter: MaybeComputedRef<TaskFilter>): UseFetchReturn<Page<Task>> {
    const dayJs = useDayJs()
    const url = computed(() => {
        const filterVal = resolveUnref(filter)
        const params = new URLSearchParams({
            page: resolveUnref(page).toString(),
        })
        if (filterVal.searchQuery) {
            params.set("searchQuery", filterVal.searchQuery)
        }

        if (filterVal.status) {
            params.set("status", filterVal.status)
        }
        if (filterVal.sortBy) {
            params.set("sort", `${filterVal.sortBy},${filterVal.direction}`)
        }
        if (filterVal.start && filterVal.end) {
            params.set("start", dayJs(filterVal.start).utc().format())
            params.set("end", dayJs(filterVal.end).utc().format())
        }
        if (filterVal.size) {
            params.set("size", filterVal.size.toString())

        }
        for (let email of filterVal.performers) {
            params.append("performers", email)
        }
        for (let type of filterVal.types) {
            params.append("types", type)
        }
        for (let labelId of filterVal.labels) {
            params.append("labels", labelId.toString())
        }
        return `/projects/${resolveUnref(project)}/tasks?${params.toString()}`
    })
    return useFetch<Page<Task>>(url, {initialData: []})
        .get()
        .json()
}

export function useTaskTypes(id: MaybeComputedRef<number | null>, immediate: boolean = true): UseFetchReturn<TaskType[]> {
    const url = computed(() => !!resolveUnref(id) ? `/projects/${resolveUnref(id)}/taskTypes` : '')
    return useFetch<TaskType[]>(url, {initialData: [], refetch: () => url.value.length > 0, immediate})
        .get()
        .json()
}


export function useTasksReports(id: MaybeComputedRef<number>, tasks: MaybeComputedRef<number[]>): UseFetchReturn<ReportPerformTask[]> {
    const url = computed(() => {
        const params = new URLSearchParams()
        resolveUnref(tasks).forEach((v) => params.append("tasks", v.toString()))
        return `/projects/${resolveUnref(id)}/performTasks/report?${params.toString()}`
    })
    return useFetch<ReportPerformTask[]>(url, {initialData: []})
        .get()
        .json()
}


export function useAddTask(id: MaybeComputedRef<number>, newTask: MaybeComputedRef<NewTask>): UseFetchReturn<Task> {
    const url = computed(() => `/projects/${resolveUnref(id)}/tasks`)
    return useFetch<Task>(url, {immediate: false})
        .post(newTask)
        .json()
}

export function useUpdateTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, newTask: MaybeComputedRef<UpdateTask>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/tasks/${resolveUnref(taskId)}`)
    return useFetch<null>(url, {immediate: false})
        .put(newTask)
}

export function useCloseTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/tasks/${resolveUnref(taskId)}/close`)
    return useFetch<null>(url, {immediate: false})
        .post()
}

export function useOpenTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/tasks/${resolveUnref(taskId)}/open`)
    return useFetch<null>(url, {immediate: false})
        .post()
}

export function useDeleteTasks(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number[]>): UseFetchReturn<null> {
    const url = computed(() => {
        const params = new URLSearchParams()
        resolveUnref(taskId).forEach(id => params.append('ids', id.toString()))
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/tasks?${params.toString()}`
        } else {
            return ``
        }
    })
    return useFetch<null>(url, {immediate: false})
        .delete()
}

export function useDeleteTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/tasks/${resolveUnref(taskId)}`)
    return useFetch<null>(url, {immediate: false})
        .delete()
}

export function useAddTaskLabels(id: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, labels: MaybeComputedRef<number[]>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}/tasks/${resolveUnref(taskId)}/labels`)
    return useFetch<null>(url, {immediate: false})
        .post(labels, 'json')
}

export function useAddTasksLabels(id: MaybeComputedRef<number>, taskId: MaybeComputedRef<number[]>, labels: MaybeComputedRef<number[]>): UseFetchReturn<null> {
    const url = computed(() => {
        const params = new URLSearchParams()
        resolveUnref(taskId).forEach(id => params.append('ids', id.toString()))
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(id)}/tasks/labels?${params.toString()}`
        } else {
            return ``
        }
    })
    return useFetch<null>(url, {immediate: false})
        .post(labels, 'json')
}

export function useAddTaskPerformers(id: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, performers: MaybeComputedRef<string[]>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}/tasks/${resolveUnref(taskId)}/assign`)
    return useFetch<null>(url, {immediate: false})
        .post(performers, 'json')
}

export function useAddTasksPerformers(id: MaybeComputedRef<number>, taskId: MaybeComputedRef<number[]>, performers: MaybeComputedRef<string[]>): UseFetchReturn<null> {
    const url = computed(() => {
        const params = new URLSearchParams()
        resolveUnref(taskId).forEach(id => params.append('ids', id.toString()))
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(id)}/tasks/assign?${params.toString()}`
        } else {
            return ``
        }
    })
    return useFetch<null>(url, {immediate: false})
        .post(performers, 'json')
}


export function useCopyTasks(id: MaybeComputedRef<number>, taskId: MaybeComputedRef<number[]>, projectId: MaybeComputedRef<number>, copy: MaybeComputedRef<CopyTask>): UseFetchReturn<null> {
    const url = computed(() => {
        const params = new URLSearchParams()
        params.append('to', resolveUnref(projectId).toString());
        resolveUnref(taskId).forEach(id => params.append('ids', id.toString()))
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(id)}/tasks/copy?${params.toString()}`
        } else {
            return ``
        }
    })
    return useFetch<null>(url)
        .post(copy, 'json')
}

export function useCopyProject(id: MaybeComputedRef<number>, projectsId: MaybeComputedRef<number[]>, copy: MaybeComputedRef<CopyProject>): UseFetchReturn<null> {
    const url = computed(() => {
        const params = new URLSearchParams()
        resolveUnref(projectsId).forEach(id => params.append('projectIds', id.toString()))
        const idProjectType = resolveUnref(id);
        if (idProjectType) {
            return `/projectTypes/${idProjectType}/copy?${params.toString()}`
        } else {
            return ``
        }
    })
    return useFetch<null>(url)
        .post(copy, 'json')
}

export function useMigrateProjectType(source: MaybeComputedRef<number>, target: MaybeComputedRef<number>): UseFetchReturn<{ movedCount: number }> {
    const url = computed(() => {
        const params = new URLSearchParams()
        params.append('source', resolveUnref(source).toString());
        params.append('target', resolveUnref(target).toString());
        const sourceId = resolveUnref(source);
        const targetId = resolveUnref(target);
        if (sourceId != null && targetId != null) {
            return `/projectTypes/migrate?${params.toString()}`
        } else {
            return ``
        }
    })
    return useFetch<{ movedCount: number }>(url)
        .post()
        .json()
}

export function useTaskType(projectId: MaybeComputedRef<number>, taskTypeId: MaybeComputedRef<number | null>): UseFetchReturn<TaskType> {
    const url = computed(() => {
        const taskType = resolveUnref(taskTypeId);
        if (taskType) {
            return `/projects/${resolveUnref(projectId)}/taskTypes/${taskType}`
        } else {
            return ''
        }
    })
    return useFetch<TaskType>(url, {refetch: () => url.value.length > 0})
        .get()
        .json()
}

export function useTask(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<TaskDescription> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/tasks/${task}`
        } else {
            return ``
        }
    })
    return useFetch<TaskDescription>(url, {refetch: () => !!url.value})
        .get()
        .json()
}

export function useFindSimilarPhotos(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<PhotoSimilarity[]> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/tasks/${task}/photos/similar`
        } else {
            return ``
        }
    })
    return useFetch<PhotoSimilarity[]>(url, {refetch: () => !!url.value, immediate: true})
        .get()
        .json()
}


export function useSharedTask(token: MaybeComputedRef<string>): UseFetchReturn<SharedTask> {
    const url = computed(() => {
        const task = resolveUnref(token);
        if (task) {
            return `/shared/task?token=${task}`
        } else {
            return ``
        }
    })
    return useFetch<SharedTask>(url, {refetch: () => !!url.value})
        .get()
        .json()
}

export function useTaskLink(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<TaskLink> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/tasks/${task}/link`
        } else {
            return ``
        }
    })
    return useFetch<TaskLink>(url, {refetch: () => !!url.value, immediate: false})
        .get()
        .json()
}

export function useTaskLogs(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, page: MaybeComputedRef<number>): UseFetchReturn<Page<TaskLog>> {
    const url = computed(() => {
        const task = resolveUnref(taskId);
        if (task) {
            return `/projects/${resolveUnref(projectId)}/tasks/${resolveUnref(taskId)}/logs?page=${resolveUnref(page)}`

        } else {
            return ``
        }
    })
    return useFetch<Page<TaskLog>>(url, {refetch: () => !!url.value})
        .get()
        .json()
}


export function useTaskPhotos(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, page: MaybeComputedRef<number>): UseFetchReturn<Page<Photo>> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/tasks/${resolveUnref(taskId)}/photos?page=${resolveUnref(page)}`)
    return useFetch<Page<Photo>>(url)
        .get()
        .json()
}

export function useAllTaskPhotos(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<Page<ReportPhoto>> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/tasks/${resolveUnref(taskId)}/photos?page=0&size=100000`)
    return useFetch<Page<Photo>>(url)
        .get()
        .json()
}

export function usePerformTaskPhotos(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>, page: MaybeComputedRef<number>): UseFetchReturn<Page<Photo>> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/performTasks/${resolveUnref(taskId)}/photos?page=${resolveUnref(page)}`)
    return useFetch<Page<Photo>>(url)
        .get()
        .json()
}


export function useAllPerformTaskPhotos(projectId: MaybeComputedRef<number>, taskId: MaybeComputedRef<number>): UseFetchReturn<Page<ReportPhoto>> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/performTasks/${resolveUnref(taskId)}/photos?page=0&size=100000`)
    return useFetch<Page<ReportPhoto>>(url)
        .get()
        .json()
}

export function useDeletePhoto(projectId: MaybeComputedRef<number>, photoId: MaybeComputedRef<number>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/photos/${resolveUnref(photoId)}`)
    return useFetch<null>(url)
        .delete()
}


export function useUpdatePhotoDate(projectId: MaybeComputedRef<number>, photoId: MaybeComputedRef<number>, date: MaybeComputedRef<string>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/photos/${resolveUnref(photoId)}/date`)
    return useFetch<null>(url)
        .put({
            date: resolveUnref(date)
        })
}

export function useUpdatePhotoOnlyDate(projectId: MaybeComputedRef<number>, photoId: MaybeComputedRef<number>, date: MaybeComputedRef<string>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/photos/${resolveUnref(photoId)}/onlyDate`)
    return useFetch<null>(url)
        .put({
            date: resolveUnref(date)
        })
}

export function useUpdateOnlyPhotoLocation(projectId: MaybeComputedRef<number>, photo: MaybeComputedRef<Photo>, latitude: MaybeComputedRef<number | null>, longitude: MaybeComputedRef<number | null>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/photos/${resolveUnref(photo)?.id}/location`)
    return useFetch<null>(url)
        .put(computed(() => ({
            latitude: resolveUnref(latitude),
            longitude: resolveUnref(longitude),
        })), 'json')
}

export function useUpdatePhotoCaption(projectId: MaybeComputedRef<number>, photoId: MaybeComputedRef<number>, caption: MaybeComputedRef<boolean>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/photos/${resolveUnref(photoId)}/caption`)
    return useFetch<null>(url)
        .put({
            caption: resolveUnref(caption)
        })
}

export function useUpdatePhotoLocation(projectId: MaybeComputedRef<number>, photo: MaybeComputedRef<Photo>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/photos/${resolveUnref(photo)?.id}/location`)
    return useFetch<null>(url, {immediate: false})
        .put(computed(() => ({
            latitude: resolveUnref(photo)?.latitude,
            longitude: resolveUnref(photo)?.longitude,
        })), 'json')
}




export function useUpdateTaskType(projectId: MaybeComputedRef<number>, taskTypeId: MaybeComputedRef<number>, updateTaskType: UpdateTaskType): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/taskTypes/${resolveUnref(taskTypeId)}`)
    return useFetch<null>(url, {immediate: false})
        .put(updateTaskType)
}

export function useUpdateTaskTypeProperties(projectId: MaybeComputedRef<number>, taskTypeId: MaybeComputedRef<number>, properties: MaybeComputedRef<TaskPropertyDescription[]>): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(projectId)}/taskTypes/${resolveUnref(taskTypeId)}/properties`)
    return useFetch<null>(url, {immediate: false})
        .put(properties, 'json')
}

export function useAddTaskTypes(id: MaybeComputedRef<number>, taskType: NewTaskType): UseFetchReturn<null> {
    const url = computed(() => `/projects/${resolveUnref(id)}/taskTypes`)
    return useFetch<null>(url, {immediate: false})
        .post(taskType)
}

export function useTaskSearch(query: MaybeComputedRef<string>,): UseFetchReturn<Page<ProjectTask>> {
    const url = computed(() => `/search?query=${resolveUnref(query)}`)
    return useFetch<Page<ProjectTask>>(url, {immediate: false})
        .get()
        .json()
}
